import { useEffect, useState } from 'react';
import {
  Activity, Wifi, WifiOff, RefreshCw, Send, Inbox,
  Clock, AlertCircle, CheckCircle2, Loader2,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const fetchJson = async (path) => {
  const r = await fetch(`${API}${path}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json();
};

const fmtTimeAgo = (sec) => {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s lalu`;
  if (sec < 3600) return `${Math.floor(sec / 60)} min lalu`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} jam lalu`;
  return `${Math.floor(sec / 86400)} hari lalu`;
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'red' }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-semibold text-gray-800 mt-1">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className={`p-2 rounded-lg bg-${color}-50`}>
        <Icon className={`w-5 h-5 text-${color}-700`} />
      </div>
    </div>
  </div>
);

const ConnectionBadge = ({ health }) => {
  if (!health) return null;
  const isOnline = health.is_connected;
  return (
    <div data-testid="wm-connection-badge"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${isOnline ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
      {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
      {isOnline ? 'Wingman Online' : 'Wingman Offline'}
      {health.last_seen_seconds_ago != null && (
        <span className="text-xs opacity-70">· last {fmtTimeAgo(health.last_seen_seconds_ago)}</span>
      )}
    </div>
  );
};

export default function WingmanActivityPage() {
  const [health, setHealth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [events, setEvents] = useState([]);
  const [snapshotDate, setSnapshotDate] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('summary'); // summary | activity | events

  const loadAll = async () => {
    setLoading(true);
    try {
      const [h, s, r, e] = await Promise.all([
        fetchJson('/api/hr/wingman-activity/health'),
        fetchJson('/api/hr/wingman-activity/daily-summary?days=14'),
        fetchJson('/api/hr/wingman-activity/recent?limit=80'),
        fetchJson('/api/hr/wingman-activity/event-recent?limit=50'),
      ]);
      setHealth(h);
      setSummary(s);
      setRecent(r.audit || []);
      setEvents(e.events || []);
    } catch (err) {
      console.error('load failed', err);
    } finally {
      setLoading(false);
    }
  };

  const openSnapshot = async (date) => {
    setSnapshotDate(date);
    setSnapshot(null);
    try {
      const s = await fetchJson(`/api/hr/wingman-activity/daily-snapshot?date_iso=${date}`);
      setSnapshot(s);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const chartData = summary?.days
    ? [...summary.days].reverse().map((d) => ({
        date: d.date.slice(5),
        calls: d.calls.total,
        events: d.events.total,
        errors: d.calls.errors,
      }))
    : [];

  return (
    <div className="space-y-5" data-testid="wingman-activity-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-lg bg-red-700">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Aktiviti Wingman HR Agent</h1>
            <p className="text-sm text-gray-500 mt-0.5">Pantau aktiviti & laporan harian yang dihantar ke HURE Agent</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ConnectionBadge health={health} />
          <button
            onClick={loadAll}
            data-testid="wm-refresh-btn"
            disabled={loading}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Connection Card */}
      {health && (
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="wm-conn-card">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Wifi className="w-4 h-4" /> Status Sambungan
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Last Seen</p>
              <p className="font-medium text-gray-800">{fmtTimeAgo(health.last_seen_seconds_ago)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{health.last_seen_at?.slice(0, 19) || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last IP</p>
              <p className="font-mono text-xs text-gray-800">{health.last_seen_ip || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last Endpoint</p>
              <p className="font-mono text-[10px] text-gray-700 break-all">{health.last_seen_path || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">User Agent</p>
              <p className="text-xs text-gray-700 break-all">{(health.last_seen_user_agent || '—').slice(0, 40)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Inbox} label="Total Calls Hari Ini" value={health?.total_calls_today ?? '—'}
          sub={`${health?.total_calls_24h ?? 0} dalam 24 jam`} />
        <StatCard icon={Send} label="Reports Pulled (14d)" value={summary?.totals?.report_pulls ?? '—'}
          sub="Daily attendance broadcast" color="emerald" />
        <StatCard icon={Activity} label="Clock-Ins Received" value={summary?.totals?.clock_ins ?? '—'}
          sub="Via WhatsApp Wingman" color="blue" />
        <StatCard icon={AlertCircle} label="Errors Today"
          value={`${health?.errors_today ?? 0} (${health?.error_rate_pct ?? 0}%)`}
          sub="Failed API calls" color={health?.errors_today ? 'red' : 'gray'} />
      </div>

      {/* Activity Chart */}
      {chartData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Trend 14 Hari</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="calls" fill="#dc2626" name="API Calls" />
              <Bar dataKey="events" fill="#10b981" name="Events Sent" />
              <Bar dataKey="errors" fill="#f59e0b" name="Errors" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="flex border-b border-gray-100">
          {[
            { id: 'summary', label: 'Laporan Harian', icon: Clock },
            { id: 'activity', label: 'Activity Log', icon: Inbox },
            { id: 'events', label: 'Event Deliveries', icon: Send },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`wm-tab-${t.id}`}
              className={`px-4 py-3 text-sm font-medium flex items-center gap-1.5 border-b-2 transition ${tab === t.id ? 'border-red-700 text-red-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="p-4">
          {/* TAB 1: Daily summary */}
          {tab === 'summary' && summary && (
            <div className="overflow-x-auto" data-testid="wm-daily-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left p-2">Tarikh</th>
                    <th className="text-right p-2">Calls</th>
                    <th className="text-right p-2">Reports</th>
                    <th className="text-right p-2">Clock-Ins</th>
                    <th className="text-right p-2">Leaves</th>
                    <th className="text-right p-2">Events</th>
                    <th className="text-right p-2">Errors</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.days.map((d) => {
                    const noActivity = d.calls.total === 0 && d.events.total === 0;
                    return (
                      <tr key={d.date}
                        className={`hover:bg-gray-50 ${d.is_today ? 'bg-amber-50/40' : ''}`}>
                        <td className="p-2">
                          <span className={`font-medium ${d.is_today ? 'text-amber-700' : 'text-gray-800'}`}>
                            {d.is_today && '⭐ '}{d.date}
                          </span>
                          <span className="text-xs text-gray-400 ml-1">({d.weekday})</span>
                        </td>
                        <td className="p-2 text-right tabular-nums">{d.calls.total}</td>
                        <td className="p-2 text-right tabular-nums text-emerald-700">{d.calls.report_pulls || '—'}</td>
                        <td className="p-2 text-right tabular-nums text-blue-700">{d.calls.clock_ins || '—'}</td>
                        <td className="p-2 text-right tabular-nums">{d.calls.leave_applies || '—'}</td>
                        <td className="p-2 text-right tabular-nums">
                          {d.events.total > 0 ? (
                            <span>
                              {d.events.total}
                              {d.events.failed > 0 && <span className="text-red-600 text-xs ml-1">({d.events.failed} fail)</span>}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="p-2 text-right tabular-nums">
                          {d.calls.errors > 0 ? <span className="text-red-600">{d.calls.errors}</span> : '—'}
                        </td>
                        <td className="p-2 text-center">
                          <button
                            onClick={() => openSnapshot(d.date)}
                            disabled={noActivity}
                            className="text-xs text-red-700 hover:underline disabled:text-gray-300 disabled:no-underline"
                            data-testid={`wm-snapshot-${d.date}`}
                          >
                            Drill-down →
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 2: Activity log */}
          {tab === 'activity' && (
            <div className="overflow-x-auto" data-testid="wm-activity-log">
              {recent.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Belum ada aktiviti.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="text-left p-2">Time</th>
                      <th className="text-left p-2">Method</th>
                      <th className="text-left p-2">Path</th>
                      <th className="text-right p-2">Status</th>
                      <th className="text-right p-2">Duration</th>
                      <th className="text-left p-2">IP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recent.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-2 font-mono text-[10px] text-gray-500">{r.timestamp?.slice(11, 19)} <span className="text-gray-400">UTC</span></td>
                        <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.method === 'POST' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{r.method}</span></td>
                        <td className="p-2 font-mono text-[11px] text-gray-700">{r.path?.replace('/api/wingman', '')}</td>
                        <td className="p-2 text-right">
                          <span className={`text-xs font-semibold ${r.status_code >= 400 ? 'text-red-600' : r.status_code >= 200 ? 'text-emerald-600' : 'text-gray-500'}`}>{r.status_code}</span>
                        </td>
                        <td className="p-2 text-right text-xs text-gray-500">{r.duration_ms}ms</td>
                        <td className="p-2 font-mono text-[10px] text-gray-500">{r.client_ip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* TAB 3: Event deliveries */}
          {tab === 'events' && (
            <div className="overflow-x-auto" data-testid="wm-events-log">
              {events.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  Belum ada event delivery. Setup webhook URL di /api/wingman/webhook/event/config dulu.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="text-left p-2">Time</th>
                      <th className="text-left p-2">Event Type</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-right p-2">HTTP Code</th>
                      <th className="text-left p-2">Error / Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {events.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-2 font-mono text-[10px] text-gray-500">{r.delivered_at?.slice(11, 19)} UTC</td>
                        <td className="p-2 text-xs font-medium text-gray-800">{r.event_type}</td>
                        <td className="p-2">
                          {r.status === 'ok' && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] flex items-center gap-1 w-fit"><CheckCircle2 className="w-3 h-3" /> OK</span>}
                          {r.status === 'failed' && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] flex items-center gap-1 w-fit"><AlertCircle className="w-3 h-3" /> Failed</span>}
                          {r.status === 'skipped' && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] w-fit">Skipped</span>}
                        </td>
                        <td className="p-2 text-right text-xs">{r.http_code || '—'}</td>
                        <td className="p-2 text-xs text-gray-500 truncate max-w-md">{r.error || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Snapshot modal */}
      {snapshotDate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="wm-snapshot-modal">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold text-gray-800">Snapshot: {snapshotDate}</h3>
              <button onClick={() => { setSnapshotDate(null); setSnapshot(null); }}
                data-testid="wm-snapshot-close" className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="p-4">
              {!snapshot ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <StatCard icon={Inbox} label="API Calls" value={snapshot.summary.audit_calls} />
                    <StatCard icon={Send} label="Events Sent" value={snapshot.summary.events_delivered} color="emerald" />
                    <StatCard icon={Clock} label="Clock Records" value={snapshot.summary.clock_records} color="blue" />
                  </div>
                  {snapshot.attendance.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Attendance Records</h4>
                      <div className="overflow-x-auto border border-gray-100 rounded-lg max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2">Staff ID</th>
                              <th className="text-left p-2">Time In</th>
                              <th className="text-left p-2">Time Out</th>
                              <th className="text-left p-2">Status</th>
                              <th className="text-left p-2">Source</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {snapshot.attendance.slice(0, 50).map((a, i) => (
                              <tr key={i}>
                                <td className="p-2 font-mono text-[10px]">{a.staff_id?.slice(0, 8)}…</td>
                                <td className="p-2">{a.clock_in_time?.slice(11, 16) || '—'}</td>
                                <td className="p-2">{a.clock_out_time?.slice(11, 16) || '—'}</td>
                                <td className="p-2">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${a.status === 'late' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {a.status}
                                  </span>
                                </td>
                                <td className="p-2 text-[10px] text-gray-500">{a.source}{a.auto_clock_out ? ' (auto)' : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
